import React from 'react';

import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { useLocale } from 'next-intl';
import { useTranslations } from 'next-intl';

import Table from './Table';
import { HeaderCell } from './Table.styled';
import {
  solventsAggregate,
  rangeAggregate,
  notAvailable,
  priceMedianToRange,
} from '../../utils';
import BooleanValue, { booleanAggregate } from '../cells/BooleanValue';
import {
  TextValue,
  TemperatureValue,
  StrengthValue,
  ForceValue,
} from '../cells/CellValues';
import CellWrapper, { SolventsCellWrapper } from '../cells/CellWrapper';
import RowHeaderCell from '../cells/RowHeaderCell';
import RowHeaderSubrowCell from '../cells/RowHeaderSubrowCell';
import ScaleValue, { generateScaleAggregate } from '../cells/ScaleValue';
import { useMaterialsTableContext } from '../MaterialsTableContext';
import messages from './messages';
import Icon from '@/ui/components/Icon';
import Link from '@/ui/components/Link';
import { urlBuilder } from '@/utils/urlBuilder';

/**
 * @todo translate
 * @todo tooltip
 */
export default function TableWithColumns({ exporting = false }) {
  const theme = useTheme();
  const { filteredTableData } = useMaterialsTableContext();
  const translate = useTranslations();

  const locale = useLocale();

  const groupColors = {
    even: theme.palette.gray.light,
    odd: '#eee',
  };

  const columns = React.useMemo(
    () => [
      {
        Header: (
          <HeaderCell
            $group={true}
            style={{ backgroundColor: groupColors.odd }}
          >
            <span>MATERIAL</span>
          </HeaderCell>
        ),
        id: 'material-group',
        columns: [
          {
            Header: (
              <HeaderCell
                $zIndex="auto"
                style={{ backgroundColor: groupColors.odd }}
              >
                {/* <span>
                  {translate('app-containers-MaterialsPage-material')}
                </span> */}
                <Tooltip
                  title={translate(messages.materialTooltip)}
                  placement="bottom"
                  arrow
                >
                  <Icon name="info-circle" />
                </Tooltip>
              </HeaderCell>
            ),
            accessor: 'material', // accessor is the "key" in the data
            sticky: 'left',
            width: 210,
            Cell: TextValue,
            GroupedCell: ({ value, ...rest }) => {
              // const subRows = rest.row.subRows;
              const links = rest.row.subRows
                .filter(r => !!r.original.post?.slug)
                .map(r => r.original.post);

              const uniqueLinks = links.filter(
                (link, index, self) =>
                  index ===
                  self.findIndex(
                    t => t?.original?.post?.slug === link?.original?.post?.slug
                  )
              );

              return (
                <RowHeaderCell value={value}>
                  <Tooltip
                    title={translate('icon-moreInfo')}
                    placement="right"
                    arrow
                  >
                    {uniqueLinks.map((link, index) => (
                      <Link
                        // target="_blank"
                        href={urlBuilder({ locale, item: link })}
                        key={index}
                      >
                        <Icon
                          name="info-circle"
                          onClick={e => e.stopPropagation()}
                        />
                      </Link>
                    ))}
                  </Tooltip>
                </RowHeaderCell>
              );
            },
            Placeholder: ({ row }) => <RowHeaderSubrowCell row={row} />,
          },
        ],
      },
      // HARDWARE group with the three columns underneath
      {
        Header: (
          <HeaderCell
            $group={true}
            style={{ backgroundColor: groupColors.even }}
          >
            <span>HARDWARE</span>
          </HeaderCell>
        ),
        id: 'hardware-group',
        columns: [
          {
            Header: (
              <HeaderCell style={{ backgroundColor: groupColors.even }}>
                {translate('app-containers-MaterialsPage-enclosure-short')}
                <Tooltip
                  title={translate(messages.enclosureTooltip)}
                  placement="bottom"
                  arrow
                >
                  <Icon name="info-circle" />
                </Tooltip>
              </HeaderCell>
            ),
            accessor: 'enclosure',
            Cell: ({ value }) => (
              <BooleanValue $left={true} value={value} reversed />
            ),
            aggregate: booleanAggregate,
            width: 100,
          },
          {
            Header: (
              <HeaderCell style={{ backgroundColor: groupColors.even }}>
                {translate('app-containers-MaterialsPage-dryBox-short')}
                <Tooltip
                  title={translate(messages.dryBoxTooltip)}
                  placement="bottom"
                  arrow
                >
                  <Icon name="info-circle" />
                </Tooltip>
              </HeaderCell>
            ),
            accessor: 'dry_box',
            Cell: ({ value, row }) => (
              <BooleanValue
                $left={true}
                value={value}
                reversed
                peiExtra={row?.values?.material === 'PEI'}
              />
            ),
            aggregate: booleanAggregate,
            width: 100,
          },
          {
            Header: (
              <HeaderCell style={{ backgroundColor: groupColors.even }}>
                {translate('app-containers-MaterialsPage-hardenedNozzle-short')}
                <Tooltip
                  title={translate(messages.hardenedNozzleTooltip)}
                  placement="bottom"
                  arrow
                >
                  <Icon name="info-circle" />
                </Tooltip>
              </HeaderCell>
            ),
            accessor: 'nozzle_type_hardened',
            Cell: ({ value }) => (
              <BooleanValue $left={true} value={value} reversed />
            ),
            aggregate: booleanAggregate,
            Tooltip: () => <div />,
            width: 100,
          },
        ],
      },
      // TEMPERATURES group with the three columns underneath
      {
        Header: (
          <HeaderCell
            $group={true}
            style={{ backgroundColor: groupColors.odd }}
          >
            <span>TEMPERATURES</span>
          </HeaderCell>
        ),
        id: 'temperatures-group',
        columns: [
          {
            Header: (
              <HeaderCell style={{ backgroundColor: groupColors.odd }}>
                {translate('app-containers-MaterialsPage-nozzleTemp-short')}
                {/* <small>(+-10 °C)</small> */}
                <Tooltip
                  title={translate(messages.nozzleTempTooltip)}
                  placement="bottom"
                  arrow
                >
                  <Icon name="info-circle" />
                </Tooltip>
              </HeaderCell>
            ),
            accessor: 'nozzle_temp',
            Cell: TemperatureValue,
            aggregate: rangeAggregate,
            width: 100,
          },
          {
            Header: (
              <HeaderCell style={{ backgroundColor: groupColors.odd }}>
                {translate('app-containers-MaterialsPage-bedTemp-short')}
                {/* <small>(+-10 °C)</small> */}
                <Tooltip
                  title={translate(messages.bedTempTooltip)}
                  placement="bottom"
                  arrow
                >
                  <Icon name="info-circle" />
                </Tooltip>
              </HeaderCell>
            ),
            accessor: 'bed_temp',
            Cell: TemperatureValue,
            aggregate: rangeAggregate,
            width: 100,
          },
        ],
      },
      // Sheets group with the three columns underneath
      {
        Header: (
          <HeaderCell
            $group={true}
            $center={true}
            style={{ backgroundColor: groupColors.even }}
          >
            <span>SHEETS</span>
          </HeaderCell>
        ),
        id: 'sheets-group',
        columns: [
          {
            Header: (
              <HeaderCell
                $center={true}
                style={{ backgroundColor: groupColors.even }}
              >
                {translate('app-containers-MaterialsPage-texturedSheet-short')}
                <Tooltip
                  title={translate(messages.texturedSheetTooltip)}
                  placement="bottom"
                  arrow
                >
                  <Icon name="info-circle" />
                </Tooltip>
              </HeaderCell>
            ),
            accessor: 'plate_textured',
            width: 115,
            aggregate: booleanAggregate,
            Cell: BooleanValue,
          },
          {
            Header: (
              <HeaderCell
                $center={true}
                style={{ backgroundColor: groupColors.even }}
              >
                {translate('app-containers-MaterialsPage-smoothSheet-short')}
                <Tooltip
                  title={translate(messages.smoothSheetTooltip)}
                  placement="bottom"
                  arrow
                >
                  <Icon name="info-circle" />
                </Tooltip>
              </HeaderCell>
            ),
            accessor: 'plate_smooth',
            width: 115,
            aggregate: booleanAggregate,
            Cell: BooleanValue,
          },
          {
            Header: (
              <HeaderCell
                $center={true}
                style={{ backgroundColor: groupColors.even }}
              >
                {translate('app-containers-MaterialsPage-satinSheet-short')}
                <Tooltip
                  title={translate(messages.texturedSheetTooltip)}
                  placement="bottom"
                  arrow
                >
                  <Icon name="info-circle" />
                </Tooltip>
              </HeaderCell>
            ),
            accessor: 'plate_satin',
            width: 115,
            aggregate: booleanAggregate,
            Cell: BooleanValue,
          },
          {
            Header: (
              <HeaderCell
                $center={true}
                style={{ backgroundColor: groupColors.even }}
              >
                {translate('app-containers-MaterialsPage-nylonSheet-short')}
                <Tooltip
                  title={translate(messages.smoothSheetTooltip)}
                  placement="bottom"
                  arrow
                >
                  <Icon name="info-circle" />
                </Tooltip>
              </HeaderCell>
            ),
            accessor: 'plate_nylon',
            width: 115,
            aggregate: booleanAggregate,
            Cell: BooleanValue,
          },
          {
            Header: (
              <HeaderCell
                $center={true}
                style={{ backgroundColor: groupColors.even }}
              >
                {translate('app-containers-MaterialsPage-ppSheet-short')}
                <Tooltip
                  title={translate(messages.smoothSheetTooltip)}
                  placement="bottom"
                  arrow
                >
                  <Icon name="info-circle" />
                </Tooltip>
              </HeaderCell>
            ),
            accessor: 'plate_pp',
            width: 115,
            aggregate: booleanAggregate,
            Cell: BooleanValue,
          },
          {
            Header: (
              <HeaderCell
                $center={true}
                style={{ backgroundColor: groupColors.even }}
              >
                {translate('app-containers-MaterialsPage-htSheet-short')}
                <Tooltip
                  title={translate(messages.smoothSheetTooltip)}
                  placement="bottom"
                  arrow
                >
                  <Icon name="info-circle" />
                </Tooltip>
              </HeaderCell>
            ),
            accessor: 'plate_ht',
            width: 115,
            aggregate: booleanAggregate,
            Cell: BooleanValue,
          },
        ],
      },
      // Physical features group with the three columns underneath
      {
        Header: (
          <HeaderCell
            $group={true}
            $center={true}
            style={{ backgroundColor: groupColors.odd }}
          >
            <span>PHYSICAL FEATURES</span>
          </HeaderCell>
        ),
        id: 'physical-features-group',
        columns: [
          {
            Header: (
              <HeaderCell
                $center={true}
                style={{ backgroundColor: groupColors.odd }}
              >
                {translate('app-containers-MaterialsPage-solvents-short')}
                <Tooltip
                  title={translate(messages.solventsTooltip)}
                  placement="bottom"
                  arrow
                >
                  <Icon name="info-circle" />
                </Tooltip>
              </HeaderCell>
            ),
            accessor: 'solvents',
            Cell: ({ value }) =>
              value && value.length ? (
                <SolventsCellWrapper>
                  {value.map((v, index) => {
                    let val = v.toLowerCase().split('-');
                    val = val[val.length - 1];
                    if (!val) return;
                    return (
                      <span
                        key={index}
                        style={{
                          fontSize: '11px',
                          lineHeight: '20px',
                          display: 'inline-block',
                          color: notAvailable.includes(val)
                            ? '#EB5757'
                            : '#777',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {translate(messages[val])}
                        {index < value.length - 1 && (
                          <span
                            dangerouslySetInnerHTML={{ __html: ',&nbsp;' }}
                          ></span>
                        )}
                      </span>
                    );
                  })}
                </SolventsCellWrapper>
              ) : (
                <BooleanValue value={false} />
              ),
            aggregate: solventsAggregate,
            Aggregated: BooleanValue,
            width: 180,
          },
          {
            Header: (
              <HeaderCell
                $center={true}
                style={{ backgroundColor: groupColors.odd }}
              >
                {translate('app-containers-MaterialsPage-heatDeflection-short')}
                {/* <small>(avg. °C)</small> */}
                <Tooltip
                  title={translate(messages.heatDeflectionTooltip)}
                  placement="bottom"
                  arrow
                >
                  <Icon name="info-circle" />
                </Tooltip>
              </HeaderCell>
            ),
            accessor: 'heat_deflection',
            aggregate: generateScaleAggregate(24, 101),
            Aggregated: ({ value }) => (
              <CellWrapper>
                <ScaleValue value={value} />
              </CellWrapper>
            ),
            Cell: props => {
              if (props.row.original.material === 'HIPS') return null;
              return <TemperatureValue {...props} />;
            },
            width: 110,
          },
          {
            Header: (
              <HeaderCell
                $center={true}
                style={{ backgroundColor: groupColors.odd }}
              >
                {translate(
                  'app-containers-MaterialsPage-impactToughness-short'
                )}
                {/* <small>
                  (kJ/m<sup>2</sup>)
                </small> */}
                <Tooltip
                  title={translate(messages.impactToughnessTooltip)}
                  placement="bottom"
                  arrow
                >
                  <Icon name="info-circle" />
                </Tooltip>
              </HeaderCell>
            ),
            accessor: 'impact_toughness_charpa',
            aggregate: generateScaleAggregate(0, 100),
            Aggregated: ({ value }) => (
              <CellWrapper>
                <ScaleValue value={value} />
              </CellWrapper>
            ),
            Cell: props => {
              if (props.row.original.material === 'HIPS') return null;
              return <StrengthValue {...props} />;
            },
            width: 110,
          },
          {
            Header: (
              <HeaderCell
                $center={true}
                style={{ backgroundColor: groupColors.odd }}
              >
                {translate('app-containers-MaterialsPage-yieldStrength')}
                {/* <small>(Mpa)</small> */}
                <Tooltip
                  title={translate(messages.yieldStrengthTooltip)}
                  placement="bottom"
                  arrow
                >
                  <Icon name="info-circle" />
                </Tooltip>
              </HeaderCell>
            ),
            accessor: 'yield_strength_in_tension',
            aggregate: generateScaleAggregate(0, 63),
            Aggregated: ({ value }) => (
              <CellWrapper>
                <ScaleValue value={value} />
              </CellWrapper>
            ),
            Cell: props => {
              if (props.row.original.material === 'HIPS') return null;
              if (
                props.row.original.material === 'PP (Polypropylene)' ||
                props.row.original.material === 'Flex'
              ) {
                if (!props.value) return null;
                return (
                  <CellWrapper>
                    {props.value} <small style={{ marginLeft: 3 }}>Mpa</small>
                    <Tooltip
                      title={'ASTM D638 method'}
                      placement="bottom"
                      arrow
                    >
                      <Icon name="info-circle" />
                    </Tooltip>
                  </CellWrapper>
                );
              }
              return <ForceValue {...props} />;
            },
            width: 110,
          },
          {
            Header: (
              <HeaderCell
                $center={true}
                style={{ backgroundColor: groupColors.odd }}
              >
                {translate('app-containers-MaterialsPage-price')}
                <Tooltip
                  title={translate(messages.priceTooltip)}
                  placement="bottom"
                  arrow
                >
                  <Icon name="info-circle" />
                </Tooltip>
              </HeaderCell>
            ),
            accessor: 'price',
            Cell: ({ value, row }) => {
              let realValue: string | null = null;
              switch (locale) {
                case 'cs':
                  realValue = value ? `${value} Kč` : null;
                  break;
                case 'en':
                  realValue = row.original.price_dolars
                    ? `$${row.original.price_dolars}`
                    : null;
                  break;
                default:
                  realValue = row.original.price_euros
                    ? `${row.original.price_euros} €`
                    : null;
                  break;
              }
              return <TextValue value={realValue || ''} />;
            },
            width: 110,
            aggregate: priceMedianToRange,
            Aggregated: ({ value }) => {
              return (
                <CellWrapper>
                  <ScaleValue value={value} reversed />
                </CellWrapper>
              );
            },
          },
        ],
      },
    ],
    [filteredTableData, locale, translate, groupColors]
  );

  return <Table exporting={exporting} columns={columns} />;
}
